/**
 * PWAInstallBanner — friendly install prompt.
 *
 * - Android/Chrome/Edge: captures `beforeinstallprompt` and triggers native
 *   install dialog when "Installer" is clicked.
 * - iOS Safari: clicking "Installer" opens an in-app modal (Partager → Sur
 *   l'ecran d'accueil) instead of relying on the (non-firing) browser event.
 * - Hidden if already standalone or dismissed in last 14 days.
 */
import { useEffect, useState, useCallback } from "react";
import { X, Download, Share, Plus } from "lucide-react";

const DISMISS_KEY = "matchup_pwa_dismiss_until";
const DISMISS_DAYS = 14;

function isStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  return Boolean(window.navigator?.standalone);
}

function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /android/i.test(navigator.userAgent);
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !/crios|fxios/i.test(navigator.userAgent);
}

function isDismissedRecently() {
  try {
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return until && Date.now() < until;
  } catch {
    return false;
  }
}

function BlackLogo({ size = 44 }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.235),
        background: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontFamily: "'Liberation Sans', 'Helvetica Neue', Arial, sans-serif",
        fontSize: Math.round(size * 0.78),
        fontWeight: 900,
        letterSpacing: "-0.06em",
        lineHeight: 1,
        boxShadow: "0 4px 12px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(255,255,255,0.08)",
        flexShrink: 0,
      }}
    >
      M
    </div>
  );
}

function IOSInstructionsModal({ onClose }) {
  return (
    <div
      data-testid="ios-install-modal"
      role="dialog"
      aria-label="Instructions d'installation iOS"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10010,
        background: "rgba(0,0,0,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        animation: "pwa-fade-in 0.18s ease-out",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0a0a0a",
          border: "1px solid #1a1a1a",
          borderRadius: 18,
          padding: "20px 18px",
          maxWidth: 360,
          width: "100%",
          color: "#fff",
          position: "relative",
        }}
      >
        <button
          onClick={onClose}
          aria-label="Fermer"
          data-testid="ios-modal-close"
          style={{
            position: "absolute", top: 10, right: 10, padding: 6,
            background: "transparent", border: "none", color: "#9ca3af",
            cursor: "pointer", borderRadius: 8,
          }}
        >
          <X size={18} />
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
          <BlackLogo size={48} />
          <div>
            <div className="font-heading" style={{ fontSize: 16, fontWeight: 700 }}>
              Installer MatchUp
            </div>
            <div style={{ fontSize: 12, color: "#9ca3af" }}>
              Suis ces 2 etapes dans Safari
            </div>
          </div>
        </div>

        <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          <li style={{
            display: "flex", alignItems: "center", gap: 12,
            background: "rgba(255,255,255,0.03)", padding: "12px 14px",
            borderRadius: 12, border: "1px solid #1a1a1a",
          }}>
            <span style={{
              width: 24, height: 24, borderRadius: 999,
              background: "#22c55e", color: "#000", fontWeight: 800,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, flexShrink: 0,
            }}>1</span>
            <span style={{ fontSize: 14, lineHeight: 1.4 }}>
              Appuie sur <b>Partager</b>
            </span>
            <Share size={20} style={{ color: "#22c55e", marginLeft: "auto" }} />
          </li>

          <li style={{
            display: "flex", alignItems: "center", gap: 12,
            background: "rgba(255,255,255,0.03)", padding: "12px 14px",
            borderRadius: 12, border: "1px solid #1a1a1a",
          }}>
            <span style={{
              width: 24, height: 24, borderRadius: 999,
              background: "#22c55e", color: "#000", fontWeight: 800,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, flexShrink: 0,
            }}>2</span>
            <span style={{ fontSize: 14, lineHeight: 1.4 }}>
              Choisis <b>Sur l'ecran d'accueil</b>
            </span>
            <Plus size={20} style={{ color: "#22c55e", marginLeft: "auto" }} />
          </li>
        </ol>

        <button
          onClick={onClose}
          data-testid="ios-modal-ok"
          style={{
            width: "100%", marginTop: 16, minHeight: 44,
            background: "#1a1a1a", border: "1px solid #2a2a2a",
            color: "#fff", borderRadius: 12, fontWeight: 600,
            cursor: "pointer", fontSize: 14,
          }}
        >
          C'est compris
        </button>
      </div>

      <style>{`@keyframes pwa-fade-in { from {opacity:0} to {opacity:1} }`}</style>
    </div>
  );
}

export default function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);
  const ios = typeof window !== "undefined" && isIOS();

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 24 * 3600 * 1000));
    } catch {}
    setVisible(false);
  }, []);

  useEffect(() => {
    if (isStandalone() || isDismissedRecently()) return;

    // Android / Chrome / Edge: native install dialog via beforeinstallprompt.
    // We only attach the listener when the event is supported AND on Android-class
    // browsers, to avoid interfering with other platforms (iOS, desktop Safari, etc).
    const supportsBIP = typeof window !== "undefined" && "onbeforeinstallprompt" in window;
    let onPrompt = null;
    let onInstalled = null;

    if (supportsBIP && (isAndroid() || !isIOS())) {
      onPrompt = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setVisible(true);
      };
      window.addEventListener("beforeinstallprompt", onPrompt);

      onInstalled = () => {
        setVisible(false);
        setDeferredPrompt(null);
      };
      window.addEventListener("appinstalled", onInstalled);
    }

    // iOS Safari: no native event ever fires — surface the manual flow immediately.
    if (isIOS()) {
      setVisible(true);
    }

    return () => {
      if (onPrompt) window.removeEventListener("beforeinstallprompt", onPrompt);
      if (onInstalled) window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (ios) {
      setShowIOSModal(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {}
    setDeferredPrompt(null);
    setVisible(false);
  };

  if (!visible && !showIOSModal) return null;

  // Button is enabled on iOS (opens modal) or when we have the deferred prompt
  const installEnabled = ios || Boolean(deferredPrompt);

  return (
    <>
      {visible && (
        <div
          data-testid="pwa-install-banner"
          role="dialog"
          aria-label="Installer MatchUp"
          style={{
            position: "fixed",
            left: "12px",
            right: "12px",
            bottom: "calc(96px + env(safe-area-inset-bottom, 0px))",
            zIndex: 10001,
            background: "#0a0a0a",
            border: "1px solid #1a1a1a",
            borderRadius: 16,
            boxShadow: "0 12px 36px rgba(0,0,0,0.55)",
            padding: "16px 16px 14px",
            color: "#fff",
            animation: "pwa-slide-up 0.35s ease-out",
          }}
        >
          <button
            onClick={dismiss}
            data-testid="pwa-install-dismiss"
            aria-label="Fermer"
            style={{
              position: "absolute", top: 8, right: 8,
              background: "transparent", border: "none", color: "#9ca3af",
              padding: 6, cursor: "pointer", borderRadius: 8,
            }}
          >
            <X size={16} />
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <BlackLogo size={44} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="font-heading" style={{ fontSize: 15, fontWeight: 700 }}>
                Installer MatchUp
              </div>
              <div style={{ fontSize: 12, color: "#9ca3af", lineHeight: 1.4 }}>
                Acces direct, mode plein ecran et notifications match.
              </div>
            </div>
          </div>

          <button
            data-testid="pwa-install-btn"
            onClick={handleInstallClick}
            disabled={!installEnabled}
            style={{
              width: "100%", minHeight: 44, borderRadius: 12,
              background: installEnabled ? "#fff" : "#1a1a1a",
              border: "none", color: installEnabled ? "#000" : "#666",
              fontSize: 14, fontWeight: 700,
              cursor: installEnabled ? "pointer" : "default",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
          >
            <Download size={16} />
            Installer l'app
          </button>

          <style>{`
            @keyframes pwa-slide-up {
              from { transform: translateY(120%); opacity: 0; }
              to { transform: translateY(0); opacity: 1; }
            }
          `}</style>
        </div>
      )}

      {showIOSModal && <IOSInstructionsModal onClose={() => setShowIOSModal(false)} />}
    </>
  );
}
